import { useEffect } from 'react';
import { useLocation, useNavigate } from '@remix-run/react';
import { type Screen } from '~/services/layout/types';
import { useStableFetcher } from './use-stable-fetcher';
import { addToHistory, removeCurrentFromHistory } from './use-screens-history';

export function useScreenHooks(screen: Screen) {
  const fetcher = useStableFetcher();
  const navigate = useNavigate();
  const location = useLocation();

  const screenHooksLength = screen?.hooks?.preload_plugins?.length || 0;

  const hasScreenHooks = screenHooksLength > 0;

  useEffect(() => {
    if (!hasScreenHooks || !screen?.id) return;

    const requestUrl = location.search
      ? `${location.pathname}${location.search}`
      : location.pathname;

    fetcher.load(
      `/api/maybe-redirect?screenId=${encodeURIComponent(
        screen?.id
      )}&requestUrl=${encodeURIComponent(requestUrl)}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Handle redirects from the fetcher
  useEffect(() => {
    const data = fetcher.data as { redirectTo?: string } | undefined;

    if (data?.redirectTo) {
      // Use replace: true for /lock redirects to avoid polluting history
      const isLockRedirect = data.redirectTo.includes('/lock');

      // If redirecting to lock screen, remove the current locked screen from history
      if (isLockRedirect) {
        const currentPath = location.pathname + location.search;
        removeCurrentFromHistory(currentPath);
      }

      navigate(data.redirectTo, { replace: isLockRedirect });
    } else if (fetcher.data && hasScreenHooks) {
      // Only add to history if screen was NOT redirected (meaning it passed all hooks)
      const currentPath = location.pathname + location.search;
      addToHistory(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data]);

  const isLoading = !fetcher.data && hasScreenHooks;

  // prevent flicker when screen has hooks and is loading or redirecting to another screen
  const hasRedirect = Boolean(
    (fetcher.data as { redirectTo?: string })?.redirectTo
  );

  return isLoading || hasRedirect;
}
